<template>
  <article class="common-part-list-item" :class="{ 'common-part-list-item--phone': isPhoneBool }" role="button"
    tabindex="0" @click="$emit('click')" @keydown.enter.prevent="$emit('click')" @keydown.space.prevent="$emit('click')">
    <div class="part-media">
      <ArticleImage :size="imageSize" :relativePath="item && item.ArticleImage">
      </ArticleImage>
    </div>

    <div class="part-main">
      <div class="part-title">{{ item.ArticleDescription }}</div>
      <div class="part-number">{{ item.ArticleNumber }}</div>
      <div v-if="secondaryText" class="part-secondary">{{ secondaryText }}</div>
    </div>

    <div v-if="showPrice" class="part-price-stack">
      <div class="part-price-label">{{ $t("text.price") }}</div>
      <v-progress-circular v-if="loadingPrice" indeterminate color="primary" size="20" width="2">
      </v-progress-circular>
      <div v-else class="part-price-value" :class="{ 'part-price-value--empty': !priceLabel }">
        {{ priceLabel || "-" }}
      </div>
    </div>

    <div class="part-controls" @click.stop>
      <div class="part-quantity-stepper" :aria-label="$t('text.amount')">
        <v-btn icon size="x-small" variant="text" :disabled="quantityValue <= 1" @click.stop="changeQuantity(-1)">
          <v-icon size="16">mdi-minus</v-icon>
        </v-btn>

        <v-text-field v-model.number="item.Quantity" class="part-quantity" hide-details density="compact"
          variant="plain" type="number" min="1" :aria-label="$t('text.amount')" @blur="normalizeQuantity"
          @click.stop @keydown.stop>
        </v-text-field>

        <v-btn icon size="x-small" variant="text" color="primary" @click.stop="changeQuantity(1)">
          <v-icon size="16">mdi-plus</v-icon>
        </v-btn>
      </div>

      <v-btn color="primary" class="part-add-button" :aria-label="$t('text.add')" @click.stop="$emit('addToCart')">
        <v-icon :class="isPhoneBool ? '' : 'mr-1'">mdi-cart-plus</v-icon>
        <span v-if="!isPhoneBool">{{ $t("text.add") }}</span>
      </v-btn>
    </div>
  </article>
</template>


<script>
import http from "@/api/client";
import ArticleImage from "@/components/Cards/ArticleImage.vue";

export default {
  name: "CommonPartListItem",
  props: {
    item: Object,
    machineId: Number,
    showPrice: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    ArticleImage,
  },

  data() {
    return {
      loadingPrice: false, //here instead of on item to ensure reactivity
    };
  },

  methods: {
    ensurePrice() {
      if (!this.showPrice || !this.item || !this.machineId || this.item.CustomerArticlePrice !== null) return;

      const item = this.item;
      this.loadingPrice = true;
      item.CustomerArticlePrice = "";
      var partUrl = "api2/customer/article/" + encodeURIComponent(item.ArticleNumber) + "/price-for-machine/" + this.machineId;
      http()
        .get(partUrl)
        .then((articlePrice) => {
          item.CustomerArticlePrice = articlePrice.data;
          item.HasCustomerPrice = true;
        }).catch(() => {
          item.HasCustomerPrice = false;
        }).finally(() => {
          if (this.item === item) this.loadingPrice = false;
        });
    },

    priceText(item) {
      var price = this.getPriceValue(item);
      if (price == null || price == "" || price == 0) {
        return "";
      }

      return this.formatCurrency(price);
    },

    getPriceValue(item) {
      if (!item) return null;
      if (item.HasCustomerPrice && item.CustomerArticlePrice !== "" && item.CustomerArticlePrice != null) {
        return item.CustomerArticlePrice;
      }

      return item.ArticlePrice;
    },

    changeQuantity(increment) {
      this.item.Quantity = Math.max(1, this.quantityValue + increment);
    },

    normalizeQuantity() {
      this.item.Quantity = this.quantityValue;
    },
  },

  computed: {
    imageSize() {
      return this.isPhoneBool ? 52 : 68;
    },

    priceLabel() {
      return this.priceText(this.item);
    },

    quantityValue() {
      return Math.max(1, Number(this.item && this.item.Quantity) || 1);
    },

    secondaryText() {
      if (!this.item) return "";
      return this.item.SalesText || this.item.ArticleDescriptionLong || "";
    },
  },

  watch: {
    item: {
      immediate: true,
      handler() {
        this.loadingPrice = false;
        this.ensurePrice();
      },
    },

    machineId() {
      this.ensurePrice();
    },

    showPrice(value) {
      if (value) this.ensurePrice();
    },
  },
};
</script>

<style scoped>
.common-part-list-item {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  min-height: 104px;
  padding: 12px 16px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.common-part-list-item:hover {
  background: #f7f8fa;
}

.common-part-list-item:focus-visible {
  box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
  outline: none;
}

.part-media {
  align-items: center;
  background: #f3f5f7;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 8px;
  display: flex;
  height: 72px;
  justify-content: center;
  overflow: hidden;
  width: 72px;
}

.part-main {
  min-width: 0;
}

.part-title {
  color: #1f2937;
  display: -webkit-box;
  font-size: 15px;
  font-weight: 650;
  line-clamp: 2;
  line-height: 1.25;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.part-number {
  color: #526071;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  margin-top: 5px;
}

.part-secondary {
  color: #6b7280;
  display: -webkit-box;
  font-size: 12px;
  line-clamp: 1;
  line-height: 1.25;
  margin-top: 3px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.part-price-stack {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  min-width: 92px;
}

.part-price-label {
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.part-price-value {
  color: #1f2937;
  font-size: 15px;
  font-weight: 750;
  line-height: 1.4;
  margin-top: 2px;
  white-space: nowrap;
}

.part-price-value--empty {
  color: #9ca3af;
  font-weight: 600;
}

.part-controls {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.part-quantity-stepper {
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  display: flex;
  height: 36px;
  overflow: hidden;
}

.part-quantity {
  flex: 0 0 42px;
  max-width: 42px;
}

.part-quantity :deep(input) {
  font-size: 14px;
  font-weight: 700;
  min-height: 32px;
  padding: 0;
  text-align: center;
}

.part-quantity :deep(input::-webkit-inner-spin-button),
.part-quantity :deep(input::-webkit-outer-spin-button) {
  margin: 0;
}

.part-add-button {
  min-width: 104px;
}

.common-part-list-item--phone {
  align-items: start;
  gap: 10px 12px;
  grid-template-columns: auto minmax(0, 1fr);
  min-height: 154px;
  padding: 12px;
}

.common-part-list-item--phone .part-media {
  height: 58px;
  width: 58px;
}

.common-part-list-item--phone .part-price-stack {
  align-items: flex-start;
  grid-column: 2;
  min-width: 0;
}

.common-part-list-item--phone .part-controls {
  grid-column: 1 / -1;
  justify-content: space-between;
  width: 100%;
}

.common-part-list-item--phone .part-add-button {
  min-width: 44px;
  padding: 0 12px;
}

@media (max-width: 420px) {
  .common-part-list-item--phone .part-controls {
    gap: 8px;
  }
}
</style>
